<!-- dashboard property stats component -->
<template>
  <div class="card dashboard-card">
    <div class="card-header d-flex align-items-center justify-content-between">
      <div>
        <h4 class="mb-0">Property Statistics</h4>
        <small class="text-muted">Summary by status</small>
      </div>

      <div class="d-flex align-items-center gap-2">
        <div class="release-filter">
          <label class="release-filter__label">Filter by release</label>
          <select
            class="form-control form-control-sm"
            v-model="filterReleaseId"
          >
            <option :value="''">All</option>
            <option v-for="s in filteredSubsections" :key="s.id" :value="s.id">
              {{ s.label }}
            </option>
          </select>
        </div>
      </div>
    </div>

    <div class="card-body">
      <!-- Summary row: Pie + Legend -->
      <div class="stats-top">
        <div class="pie-wrap">
          <div ref="pieChart"></div>
          <div ref="pieTooltip" class="pie-tooltip" />
          <div class="pie-center">
            <div class="pie-total">{{ animatedTotal }}</div>
            <div class="pie-sub">total</div>
          </div>
        </div>

        <div class="legend">
          <div v-for="item in legendItems" :key="item.key" class="legend__row">
            <span
              class="legend__dot"
              :style="{ background: item.colour }"
            ></span>
            <span class="legend__label">{{ item.label }}</span>
            <span class="legend__count">{{ item.count }}</span>
          </div>
        </div>
      </div>

      <!-- Table: available list -->
      <div class="table-header">
        <div>
          <h5 class="mb-0">Available Properties</h5>
          <small class="text-muted"
            >Showing up to {{ tableRows.length }} results</small
          >
        </div>

        <button
          class="btn btn-sm btn-sr btn-sr--primary touch-btn"
          @click="goToProperties"
        >
          View All Properties
        </button>
      </div>

      <v-table
        :columns="columns"
        :rows="tableRows"
        :show-active-toggle="false"
        @edit="editProperty($event._id)"
      />

      <div v-if="!tableRows.length" class="text-muted mt-2">
        No matching available properties.
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import Chartist from "chartist";
import VTable from "@/components/VTable";

export default {
  name: "DashboardPropertyStats",
  components: { VTable },

  data() {
    return {
      filterReleaseId: "",
      _chart: null,
      animatedTotal: 0,
    };
  },

  computed: {
    ...mapGetters(["properties", "subsections"]),

    filteredForStats() {
      let list = (this.properties || []).filter((p) => p);

      // remove display homes everywhere (stats + table)
      list = list.filter((p) => p.isDisplayHome !== true);

      // exclude inactive properties from stats + chart
      // list = list.filter(p => p.active !== false);

      if (this.filterReleaseId) {
        list = list.filter((p) => p.subsectionId === this.filterReleaseId);
      }

      return list;
    },

    counts() {
      const c = {
        Available: 0,
        "On Hold": 0,
        Sold: 0,
        "Not Released": 0,
        Other: 0,
      };

      for (const p of this.filteredForStats) {
        const status = p.status || "Other";

        // exclude inactive ONLY if status is Available
        if (status === "Available" && p.active === false) {
          continue;
        }

        if (c[status] !== undefined) {
          c[status] += 1;
        } else {
          // Coming Soon, Builder Lots, weird legacy states → Other
          c.Other += 1;
        }
      }

      return c;
    },

    filteredSubsections() {
      return this.subsections.filter((s) => {
        return s.isDisplayVillage !== true;
      });
    },

    totalCount() {
      return Object.values(this.counts).reduce((a, b) => a + b, 0);
    },

    statusColours() {
      return {
        Available: "#26C281", // sensoreal green
        "On Hold": "#D19A2A", // muted amber
        Sold: "#D85B5B", // muted red
        "Not Released": "#8B96A8", // cool grey
        Other: "#3A7BD5", // muted blue
      };
    },

    legendItems() {
      return [
        {
          key: "Available",
          label: "Available",
          count: this.counts.Available,
          colour: this.statusColours.Available,
        },
        {
          key: "On Hold",
          label: "On Hold",
          count: this.counts["On Hold"],
          colour: this.statusColours["On Hold"],
        },
        {
          key: "Sold",
          label: "Sold",
          count: this.counts.Sold,
          colour: this.statusColours.Sold,
        },
        {
          key: "Not Released",
          label: "Not Released",
          count: this.counts["Not Released"],
          colour: this.statusColours["Not Released"],
        },
        {
          key: "Other",
          label: "Other",
          count: this.counts.Other,
          colour: this.statusColours.Other,
        },
      ];
    },

    pieSeries() {
      // Chartist order
      return [
        this.counts.Available,
        this.counts["On Hold"],
        this.counts.Sold,
        this.counts["Not Released"],
        this.counts.Other,
      ];
    },

    tableRows() {
      // show available + active but NOT display homes
      return this.filteredForStats
        .filter((p) => p.status === "Available")
        .filter((p) => p.active !== false)
        .filter((p) => p.isDisplayHome !== true);
    },

    columns() {
      const goEdit = (row) => this.editProperty(row._id);

      return [
        {
          label: "Status",
          field: "status",
          options: {
            sortable: false,
            return: function (status) {
              if (status === "Not Found" || !status) return "-";

              switch (status) {
                case "Available":
                  return `<div class="status status--available">Available</div>`;

                case "Coming Soon":
                  return `<div class="status status--coming-soon">Coming Soon</div>`;

                case "On Hold":
                  return `<div class="status status--on-hold">On Hold</div>`;

                case "Sold":
                  return `<div class="status status--sold">Sold</div>`;

                case "Builder Lots":
                  return `<div class="status status--builder-lots">Builder Lots</div>`;

                case "Not Released":
                  return `<div class="status status--not-released">Not Released</div>`;

                default:
                  return `<div class="status">${status}</div>`;
              }
            },
          },
        },
        {
          label: "Label",
          field: "label",
          options: { sortable: true, callback: goEdit },
        },
        {
          label: "Price",
          field: "price",
          options: {
            sortable: true,
            return: function (value) {
              if (!value || value === "Not Found")
                return `<span class="text-muted">Not set</span>`;
              return value.toLocaleString("en-AU", {
                style: "currency",
                currency: "AUD",
                maximumSignificantDigits: 10,
              });
            },
          },
        },
      ];
    },
  },

  watch: {
    pieSeries: {
      immediate: true,
      handler() {
        this.renderPie();
      },
    },

    totalCount: {
      immediate: true,
      handler(newVal) {
        this.animateTotal(newVal);
      },
    },

    filterReleaseId() {
      // update the chart quickly when release changes
      this.renderPie();
    },
  },

  created() {
    this.$store.dispatch("FETCH_PROPERTIES");
    this.$store.dispatch("FETCH_SUBSECTIONS");
  },

  mounted() {
    // this.renderPie();
  },

  methods: {
    renderPie() {
      if (!this.$refs.pieChart) return;

      // clear previous SVG to avoid stacking / weird re-render states
      this.$refs.pieChart.innerHTML = "";

      // also hide tooltip when rebuilding chart
      var tooltip = this.$refs.pieTooltip;
      if (tooltip) tooltip.style.opacity = 0;

      var hasData = this.pieSeries.some(function (n) {
        return n > 0;
      });
      var data = { series: hasData ? this.pieSeries : [1] };

      var options = {
        donut: true,
        donutWidth: 18,
        showLabel: false,
      };

      this._chart = new Chartist.Pie(this.$refs.pieChart, data, options);

      var labels = ["Available", "On Hold", "Sold", "Not Released", "Other"];
      var seriesSnapshot = this.pieSeries.slice(); // capture current values for tooltip

      this._chart.on("draw", function (ctx) {
        if (ctx.type !== "slice") return;
        if (!ctx.element) return;
        if (!ctx.element._node) return;

        // slice animation
        if (typeof ctx.element._node.getTotalLength === "function") {
          var pathLength = ctx.element._node.getTotalLength();

          ctx.element.attr({
            "stroke-dasharray": pathLength + "px " + pathLength + "px",
            "stroke-dashoffset": pathLength + "px",
          });

          ctx.element.animate(
            {
              "stroke-dashoffset": {
                id: "anim" + ctx.index,
                dur: 1200,
                from: pathLength,
                to: 0,
                begin: ctx.index * 120,
                easing: Chartist.Svg.Easing.easeOutQuint,
                fill: "freeze",
              },
            },
            false
          );
        }

        // tooltip hover
        if (!tooltip) return;

        var el = ctx.element._node;
        var label = labels[ctx.index] || "Status";
        var value = seriesSnapshot[ctx.index];

        el.addEventListener("mouseenter", function () {
          tooltip.innerHTML = "<strong>" + label + "</strong>: " + value;
          tooltip.style.opacity = 1;
        });

        el.addEventListener("mouseleave", function () {
          tooltip.style.opacity = 0;
        });

        // position tooltip relative to pie-wrap
        el.addEventListener("mousemove", function (e) {
          var wrap = tooltip.parentElement; // .pie-wrap
          if (!wrap) return;

          var rect = wrap.getBoundingClientRect();
          tooltip.style.left = e.clientX - rect.left + 12 + "px";
          tooltip.style.top = e.clientY - rect.top + 12 + "px";
        });
      });
    },

    animateTotal(target) {
      // animate up from 0
      this.animatedTotal = 0;

      const start = 0;
      const startTime = performance.now();
      const duration = 1200;

      const tick = (now) => {
        const progress = Math.min((now - startTime) / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3); // easeOutCubic

        this.animatedTotal = Math.round(start + target * eased);

        if (progress < 1) {
          requestAnimationFrame(tick);
        } else {
          this.animatedTotal = target;
        }
      };

      requestAnimationFrame(tick);
    },

    editProperty(id) {
      this.$router.push(`/live/properties/${id}`);
    },

    goToProperties() {
      this.$router.push(`/live/properties`);
    },
  },
};
</script>

<style lang="scss" scoped>
// .dashboard-card {
//   height: 9%;
// }
/* summary row layout */
.stats-top {
  display: flex;
  gap: 24px;
  align-items: center;
  margin-bottom: 12px;
}

/* pie wrap */
.pie-wrap {
  position: relative;
  width: 140px;
  height: 140px;
  flex: 0 0 auto;
}

/* center label */
.pie-center {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}
.pie-total {
  font-weight: 700;
  font-size: 22px;
  line-height: 1;
}
.pie-sub {
  font-size: 12px;
  color: #6b7280;
}

/* make Chartist SVG size consistent */
.pie-wrap :deep(svg) {
  width: 140px;
  height: 140px;
}

/* legend */
.legend {
  flex: 1;
  min-width: 260px;
}
.legend__row {
  display: grid;
  grid-template-columns: 14px 1fr auto;
  gap: 10px;
  align-items: center;
  padding: 4px 0;
}
.legend__dot {
  width: 10px;
  height: 10px;
  border-radius: 999px;
}
.legend__label {
  font-size: 14px;
}
.legend__count {
  font-weight: 600;
}

/* filter */
.release-filter {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}
.release-filter__label {
  font-size: 11px;
  color: #6b7280;
  margin-bottom: 2px;
}

// /* reuse existing .status dot, but make it dot-only in dashboard table */
// .status--dot-only {
//   padding: 0 !important;
//   width: 15px;
//   height: 15px;
//   border-radius: 50%;
//   display: inline-block;
// }
// .status--dot-only::before {
//   left: 0 !important;
// }

.table-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 10px;
  margin-bottom: 4px;
}

/* scrollable table body */
.dashboard-card .table-wrapper {
  max-height: 300px;
  // min-height: 300px;
  overflow-y: auto;
}

.dashboard-card .table-wrapper table {
  margin: 0;
  border-collapse: separate;
}

.dashboard-card .table-wrapper thead {
  position: sticky;
  top: 0;
  z-index: 5;
}

.dashboard-card .table-wrapper tbody tr:first-child td {
  border-top: none;
}
</style>

<!-- global style overrides -->
<style lang="scss">
/* DashboardPropertyStats – force-hide VTable pagination/footer */
.dashboard-card .table-wrapper .pagination {
  display: none !important;
}
// chartlist donut override
.dashboard-card .ct-series-a .ct-slice-donut {
  stroke: #26c281;
}
.dashboard-card .ct-series-b .ct-slice-donut {
  stroke: #d19a2a;
}
.dashboard-card .ct-series-c .ct-slice-donut {
  stroke: #d85b5b;
}
.dashboard-card .ct-series-d .ct-slice-donut {
  stroke: #8b96a8;
}
.dashboard-card .ct-series-e .ct-slice-donut {
  stroke: #3a7bd5;
}

/* pie chart hover - brighten hovered slice */
.dashboard-card .ct-slice-donut {
  transition: opacity 0.2s ease, stroke-width 0.2s ease;
}

.dashboard-card .ct-series:hover .ct-slice-donut {
  opacity: 0.85;
  stroke-width: 20px !important; /* slightly thicker on hover */
}

/* pie tooltip */
.pie-tooltip {
  position: absolute;
  pointer-events: none;
  background: rgba(17, 24, 39, 0.95);
  color: #ffffff;
  font-size: 12px;
  padding: 6px 8px;
  border-radius: 4px;
  opacity: 0;
  transition: opacity 0.15s ease;
  white-space: nowrap;
  z-index: 20;
}

.pie-tooltip strong {
  color: #ffffff; /* override browser default */
  font-weight: 600;
}

.dashboard-card .table-wrapper {
  margin-top: 10px;
}
</style>
